// src/App.tsx
// REPOSITIONING PASS — dashboard route block for the Outcomes-centric IA.
//
//   - Real import: DashboardOutcomes now wired in, replacing its inline
//     placeholder stub from the prior pass.
//   - timeline/:filter — collapsed from three static sub-routes into one
//     dynamic route, matching DashboardTimeline's useParams().filter read.
//   - Remaining placeholders (DashboardApprovals, DashboardAudit,
//     DashboardSettings) still stubbed inline — not yet built.
//     DashboardOverview intentionally NOT routed; its placement in the
//     new IA is still pending a separate decision.
//   - Legacy dashboard paths (overview, qualification, vault) redirect
//     non-destructively rather than 404ing.
//
//   - FIXED — chat/:chatId renamed to chat-history/:chatId. The old path
//     collided with firebase.json's backend rewrite
//     ("/dashboard/chat/**" → fdwApi), meaning any direct navigation,
//     browser back, or page refresh on a specific-chat URL hit the
//     Express backend (which has no matching GET route at that shape)
//     instead of the SPA shell — a real 404, found live. Renaming the
//     frontend route moves it outside the colliding prefix entirely.
//     Confirmed safe: nothing in ChatScreen.tsx or ChatHistoryDrawer
//     currently navigates to this URL (chat selection from the history
//     Drawer calls loadChat(id) directly, no route change) — so this
//     rename has no other call sites to update in this pass.
//
//   - HOMEPAGE SWAP — LandingView (Product-focused copy: "Every mismatch
//     is money. We catch it.") is now the site's homepage at '/'.
//     HubView (lifecycle strip, discovery, stack architecture, comparison
//     matrix, FAQ, machine-context block) relocates to '/platform',
//     unchanged internally — still receives user/onNavigate exactly as
//     before, since nothing about its own content or props changed, only
//     its route. HubView's dashboard-aware hero CTAs ("I'm hiring" /
//     "I'm the one being hired") are being demised — LandingView does
//     NOT replicate that fork and does not take user/onNavigate props.
//
//   - ADDED — /solutions/content-teams routed to the new ContentTeamsView,
//     the search-performance detection vertical (buildlog
//     aUE3SOZej3XKNRBXLisr). Placed alongside /solutions/fintechs rather
//     than under /verticals/* — that prefix is specifically the
//     Strategy-Doc-sequenced Vertical Starting Kits list (Transactional
//     Legal first, Bookkeeping second, per the comment below), not a
//     generic "any finished vertical goes here" slot. This vertical
//     wasn't chosen via that same sequencing and sits in a weaker-
//     evidenced, more competitive cluster per this session's own
//     research — /solutions/* is the correct, lower-commitment slot for
//     it. Six other nav entries surfaced by the same header change
//     (/verticals/reconciliation, /verticals/expense,
//     /solutions/solo, /solutions/small-firms, /solutions/growing-teams,
//     /solutions/accounting-firms, /solutions/law-firms) remain
//     unrouted — pre-existing gaps, not introduced by this change,
//     deliberately left alone here.

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { UserProfile } from './types';
import { subscribeToAuthChanges } from './services/firebase';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import { LandingView } from './components/LandingView';
import { BlogView } from './components/BlogView';
import { AdminDashboard } from './components/AdminDashboard';
import { DashboardShell } from './components/DashboardShell';
import { LoginPage } from './components/LoginPage';
import { PrivacyView } from './components/PrivacyView';
import { TermsView } from './components/TermsView';
import { AboutView } from './components/AboutView';
import { RegistryView } from './components/RegistryView';
import { FdwMcpLogin } from './components/FdwMcpLogin';
import { CompareView } from './components/CompareView';
import { HowAIWorksView } from './components/HowAIWorksView';

// Dashboard sections — existing, unchanged
import { DashboardTimeline } from './components/dashboard/DashboardTimeline';
import { DashboardThreadDetail } from './components/dashboard/DashboardThreadDetail';
import { DashboardRules } from './components/dashboard/DashboardRules';
import { DashboardAnalytics } from './components/dashboard/DashboardAnalytics';
import { DashboardProfile } from './components/dashboard/DashboardProfile';
import { ChatScreen } from './components/dashboard/ChatScreen';

// Dashboard sections — new, real
import { DashboardOutcomes } from './components/dashboard/DashboardOutcomes';
import { DashboardOutcomeDetail } from './components/dashboard/DashboardOutcomeDetail';
import { DashboardHome } from './components/dashboard/DashboardHome';
import { DashboardApprovals } from './components/dashboard/DashboardApprovals';
import { DashboardAudit } from './components/dashboard/DashboardAudit';
import { DashboardDetectionFindings } from './components/dashboard/DashboardDetectionFindings';
import { DashboardDetectionTrace } from './components/dashboard/DashboardDetectionTrace';
import { DashboardEngagementDetail } from './components/dashboard/DashboardEngagementDetail';
import { DashboardEngagements } from './components/dashboard/DashboardEngagements';
import { SecurityView } from './components/SecurityView';

// ── PLACEHOLDERS ─────────────────────────────────────────────────────
// DashboardApprovals, DashboardAudit, DashboardSettings do not exist
// yet. Stubbed here so the route table is complete and the app builds.
// Replace each with a real file at ./components/dashboard/<Name>.tsx,
// matching DashboardTimeline.tsx / DashboardOutcomes.tsx conventions
// (useOutletContext or local state for user, authedFetch +
// dashboardToken pattern for API calls).
const DashboardSettings: React.FC = () => (
  <div className="text-sm text-brand-gray font-mono">
    [placeholder] DashboardSettings — account, autonomy defaults, integrations, team, billing, data permissions
  </div>
);
// ─────────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Dashboard is its own app shell — no marketing Header/Footer chrome.
  // Everything else keeps the public site frame.
  const isDashboardRoute = location.pathname.startsWith('/dashboard');

  return (
  <div className="min-h-screen bg-brand-surface text-brand-black font-sans selection:bg-brand-accent selection:text-white">

    {!isDashboardRoute && <Header user={user} />}

    {isDashboardRoute ? (
      <Routes>
        <Route
          path="/dashboard"
          element={
            authLoading ? (
              <div className="flex items-center justify-center min-h-screen text-sm text-brand-gray">
                Loading…
              </div>
            ) : user ? (
              <DashboardShell user={user} onNavigate={(view) => navigateToPath(navigate, view)} />
            ) : (
              <LoginPage onNavigate={(view) => navigateToPath(navigate, view)} />
            )
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="chat-home" element={<ChatScreen />} />
          <Route path="chat-history/:chatId" element={<ChatScreen />} />
          <Route path="outcomes" element={<DashboardOutcomes />} />
          <Route path="outcomes/new" element={<DashboardOutcomeDetail />} />
          <Route path="outcomes/:outcomeId" element={<DashboardOutcomeDetail />} />
          <Route path="engagements" element={<DashboardEngagements />} />
          <Route path="engagements/:engagementRef" element={<DashboardEngagementDetail />} />
          <Route path="timeline" element={<DashboardTimeline />} />
          <Route path="timeline/:filter" element={<DashboardTimeline />} />
          <Route path="timeline/thread/:threadId" element={<DashboardThreadDetail />} />
          <Route path="rules" element={<DashboardRules />} />
          <Route path="approvals" element={<DashboardApprovals />} />
          <Route path="detection" element={<DashboardDetectionFindings />} /> 
          <Route path="audit" element={<DashboardAudit />} />
          <Route path="detection/runs/:runId/trace" element={<DashboardDetectionTrace />} />
          <Route path="profile" element={<DashboardProfile />} />
          <Route path="analytics" element={<DashboardAnalytics />} />
          <Route path="settings" element={<DashboardSettings />} />

          {/* Legacy path redirects — non-destructive, same pattern as
              /for-services etc. on the marketing side */}
          <Route path="overview" element={<Navigate to="/dashboard" replace />} />
          <Route path="qualification" element={<Navigate to="/dashboard/rules" replace />} />
          <Route path="vault" element={<Navigate to="/dashboard/settings" replace />} />
        </Route>
      </Routes>
    ) : (
      <main className="relative">
        <Routes>
          <Route path="/" element={<LandingView />} />
          <Route path="/compare" element={<CompareView />} />
          <Route path="/how-it-works" element={<HowAIWorksView />} />
          <Route path="/blog" element={<BlogWrapper user={user} />} />
          <Route path="/blog/:postId" element={<BlogWrapper user={user} />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/about" element={<AboutView />} />
          <Route path="/registry" element={<RegistryView />} />
          <Route path="/registry/:uid" element={<RegistryView />} />
          <Route path="/security" element={<SecurityView />} />
          <Route path="/mcp/login" element={<FdwMcpLogin />} />
          <Route path="/login" element={<LoginPage onNavigate={(view) => navigateToPath(navigate, view)} />} />
          <Route path="/privacy" element={<PrivacyView />} />
          <Route path="/terms" element={<TermsView />} />
        </Routes>
      </main>
    )}

    {!isDashboardRoute && <Footer />}
  </div>
);
};

const navigateToPath = (navigate: any, view: string, id?: string) => {
  switch(view) {
    case 'BLOG':               navigate('/blog');       break;
    case 'BLOG_POST': id ? navigate(`/blog/${id}`) : navigate('/blog'); break;
    case 'ADMIN':              navigate('/admin');      break;
    case 'USER_DASHBOARD':     navigate('/dashboard');  break;
    case 'REGISTRY':           navigate('/registry');  break;
    case 'LOGIN':              navigate('/login');      break;
    default: navigate('/');
  }
};

const BlogWrapper: React.FC<{ user: UserProfile | null }> = ({ user }) => {
  const navigate = useNavigate();
  const { postId } = useParams();
  return (
    <BlogView
      viewState={postId ? 'BLOG_POST' : 'BLOG'}
      postId={postId || null}
      onNavigate={(view, id) => navigateToPath(navigate, view, id)}
      user={user}
    />
  );
};

export default App;